/**
 * Email magic link (SY-01.2) and account-link calls. The API mails a one-time
 * link to `${web}/auth/email?token=…` (dev: GET /v1/dev/mailbox); the web build
 * reads the token from the query and verify() redeems it for the current account.
 * A link conflict comes back as HTTP 409 with a LinkConflict body; it is returned
 * (not thrown) so the game can show both accounts.
 */
import { conflictBody, ROUTES, type ApiClient } from '../api.js';
import type { AuthResponse, LinkConflict, LinkedResponse, UnlinkedResponse } from '../schemas.js';
import type { IdentityName, IdentityToken } from './types.js';

/** Linked (with the merged snapshot), or a conflict the player must resolve by picking one account (SY-01.2). */
export type LinkOutcome = LinkConflict | LinkedResponse;

export const isLinkConflict = (r: unknown): r is LinkConflict => typeof r === 'object' && r !== null && (r as { conflict?: unknown }).conflict === true;

export class EmailMagicLink {
  constructor(private readonly api: ApiClient) {}

  async start(email: string): Promise<void> {
    await this.api.call('POST', ROUTES.emailStart, { email });
  }

  verify(token: string): Promise<LinkOutcome> {
    return this.api.call<LinkedResponse>('POST', ROUTES.emailVerify, { token }).catch(conflictBody(isLinkConflict));
  }

  /** Token from the landing URL (`/auth/email?token=`), or null. */
  static tokenFromUrl(href: string, param = 'token'): string | null {
    return new URL(href).searchParams.get(param);
  }
}

/**
 * POST /v1/auth/link/:provider `{ idToken, nonce?, authorizationCode?, redirectUri? }` (IdTokenLink); a 409
 * LinkConflict is returned, not thrown.
 */
export function linkIdentity(api: ApiClient, t: IdentityToken): Promise<LinkOutcome> {
  return api
    .call<LinkedResponse>('POST', ROUTES.linkIdentity(t.provider), {
      idToken: t.idToken,
      ...(t.nonce ? { nonce: t.nonce } : {}),
      ...(t.authorizationCode ? { authorizationCode: t.authorizationCode } : {}),
      ...(t.redirectUri ? { redirectUri: t.redirectUri } : {}),
    })
    .catch(conflictBody(isLinkConflict));
}

/**
 * DELETE /v1/auth/link/:provider: remove one sign-in method (email included). The API refuses the account's last one
 * (409 last_sign_in_method, thrown as ApiHttpError) and sends the player a "sign-in removed" notice.
 */
export function unlinkIdentity(api: ApiClient, provider: IdentityName | 'email'): Promise<UnlinkedResponse> {
  return api.call<UnlinkedResponse>('DELETE', ROUTES.linkIdentity(provider));
}

/** Keep one of the two accounts; the answer is a fresh session for the survivor on this device. */
export function resolveLinkConflict(api: ApiClient, resolveToken: string, keep: 'current' | 'other'): Promise<AuthResponse> {
  return api.call<AuthResponse>('POST', ROUTES.linkResolve, { resolveToken, keep });
}
